import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { AnnouncementSlugs, CardTypeCallToActions, RouteTypes } from '@app/enums/_index';
import { HasPageTitleAndMetaData, StaticPageInterface } from '@app/interfaces/_index';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  combineLatestWith,
  first,
  from,
  map,
  switchMap,
  tap,
} from 'rxjs';

import { StaticPageAdapter } from '@app/adapters/_index';
import { SubscriptionFactory } from '@app/factories/_index';
import { StaticPage, User } from '@app/models/_index';
import { AnnouncementService, SubscriptionService } from '@app/services/_index';
import { UserService } from '@app/services/user/user.service';
import { getUserGreeting } from '@app/utilities/_index';
import { getLearningBannerVisibility } from '@app/utilities/subscription-helpers';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy, HasPageTitleAndMetaData {
  isLoading: boolean;
  isLoadingSubscription: boolean;
  greeting: string;
  firstName$: Observable<string>;
  environment: any;
  userDashboardAnnouncement: StaticPage;
  currentUser: User;

  subs: Subscription[] = [];
  roleBasedLearningPathContent: StaticPage;
  freeLearningContent: StaticPage;
  showRoleBasedBox = false;
  showFreeLearningBox = true;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
    private subscriptionFactory: SubscriptionFactory,
    private staticPageAdapter: StaticPageAdapter,
    private announcementService: AnnouncementService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.environment = environment;
    const hour = new Date().getHours();
    this.greeting = getUserGreeting(hour);
    this.setTitleAndMetaData();

    this.firstName$ = this.userService.claims$.pipe(map((claims) => claims?.given_name));

    this.getRoleBasedLearningPathContent();
    this.getFreeLearningContent();
    this.getUserDashboardAnnouncement();

    this.isLoadingSubscription = true;
    // NOTE: User service handles and knows the authentication service, subscription service,
    // course service and enrollment service. This keeps the session docebo token in sync
    // on refresh or reload.
    this.subs.push(
      this.userService?.doceboUser$
        .pipe(
          first(),
          switchMap((currentUser) => {
            return from(this.subscriptionService.getActiveSubscriptions(currentUser));
          }),
          combineLatestWith(this.userService.doceboUser$.pipe(first())),
          map(([subscriptions, currentUser]) => {
            const activeSubscriptions =
              subscriptions?.map((subscription) => {
                return this.subscriptionFactory.getSubscription(subscription);
              }) || [];
            currentUser.activeSubscriptions = activeSubscriptions;
            return currentUser;
            // NOTE: To test empty or undefined user return EMPTY to trigger
            // default error message for no active subscriptions and enrolments.
            // return EMPTY;
          }),
          tap((_) => {
            this.isLoadingSubscription = false;
          }),
          catchError((err, _caught) => {
            console.error('Failed to load user enrollments and subscriptions', err);
            this.isLoadingSubscription = false;
            return EMPTY;
          })
        )
        .subscribe({
          next: (currentUser: any) => {
            this.currentUser = currentUser;
            const { showRoleBasedBox, showFreeLearningBox } = getLearningBannerVisibility(
              currentUser.activeSubscriptions
            );
            this.showRoleBasedBox = showRoleBasedBox;
            this.showFreeLearningBox = showFreeLearningBox;
            this.isLoadingSubscription = false;
          },
          error: (error) => {
            this.isLoadingSubscription = false;
            console.log(error);
          },
          complete: () => {
            // Force is loading to stop and complete in case of any unhandled errors
            this.isLoadingSubscription = false;
          },
        })
    );
  }

  getUserDashboardAnnouncement(): void {
    this.isLoading = true;
    const wordpressSlugName = AnnouncementSlugs.USER_DASHBOARD_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(wordpressSlugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          if (response && response.length > 0) {
            this.userDashboardAnnouncement = this.staticPageAdapter.adapt(response[0]);
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error loading user dashboard announcement:', error);
        },
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subs) this.subs.forEach((sub) => sub.unsubscribe());
  }

  clearSearchResults(): any {
    return { cs: true };
  }

  getRoleBasedLearningPathContent(): void {
    this.isLoading = true;
    const slug = AnnouncementSlugs.NEW_ROLE_BASED_LEARNING_PATHS_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(slug).subscribe({
        next: (res: StaticPageInterface[]) => {
          if (res && res.length > 0)
            this.roleBasedLearningPathContent = this.staticPageAdapter.adapt(res[0]);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
        },
      })
    );
  }

  getFreeLearningContent(): void {
    this.isLoading = true;
    const slug = AnnouncementSlugs.JUST_ADDED_MORE_FREE_LEARNING_CONTENT_SLUG;
    this.subs.push(
      this.announcementService.getAnnouncementContentBySlug(slug).subscribe({
        next: (res: StaticPageInterface[]) => {
          if (res && res.length > 0)
            this.freeLearningContent = this.staticPageAdapter.adapt(res[0]);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
        },
      })
    );
  }

  setTitleAndMetaData(): void {
    this.titleService.setTitle('User Dashboard | Genesys Education');
    this.metaService.updateTag({
      name: 'description',
      content:
        'See all of your Genesys Education enrollments, certifications, and recommended learning in one place.',
    });
  }

  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }
}
